import { createHash } from "node:crypto";
import { z } from "zod";
import { COSMETIC_DELTA_MAX, REPAIR_RATE_REVIEW } from "./graduation";
import { PATTERN_CANDIDATE_MIN_SAMPLE } from "./patternCandidate";
import { WRITE_TIERS } from "./writeTiers";

/**
 * The Learning Charter (B16 source 1). T3, humans-only (CLAUDE.md section 3:
 * "The Learning Charter itself, and auto-learn's own validation bars"). This
 * file holds the SHAPE and the code-side checks; the VALUES live in
 * `learning_charter_versions` (migration 0038), written by `pnpm charter
 * publish` as the migrate role and read by the learner through
 * `requireCharter` (packages/db/src/learningCharter.ts). No agent can write
 * the table: `ackra_app` holds SELECT only (appRole.ts).
 *
 * Three honest states, none of them silent (section 0):
 *  - invalid: the stored content fails this schema (`CharterInvalid`).
 *  - code mismatch: a ratified bar or the tier table differs from the code
 *    constant it was copied from (`CharterCodeMismatch`). The charter and the
 *    code must agree, or the learner does not run.
 *  - needs unmet: a bar or need that nobody has ruled is `null`
 *    (`needs_unmet`, B15.5). A null is never read as a default.
 */

export const CHARTER_SCHEMA_VERSION = 1 as const;

/** The closed list of moves the learner may never make (B16, CLAUDE.md section 3 and 4). */
export const PROHIBITED_MOVE_IDS = [
  "edit_charter",
  "edit_reward_weights",
  "write_t3",
  "wealth_pricing",
  "offer_in_automated_message",
  "learn_from_heldout",
  "refreeze_baseline",
  "self_grade_fact_confidence",
  "ship_without_citations",
  "ship_unanswered_grill",
] as const;
export type ProhibitedMoveId = (typeof PROHIBITED_MOVE_IDS)[number];

/** The five self-grill questions every proposal answers before it can ship (B16). */
export const SELF_GRILL_QUESTION_IDS = [
  "confounded",
  "counter_hypothesis",
  "if_wrong_if_right",
  "collides_owner_or_t3",
  "invalidators",
] as const;
export type SelfGrillQuestionId = (typeof SELF_GRILL_QUESTION_IDS)[number];

/** Highest authority first: where two sources disagree, the earlier one wins (B16). */
export const TRUTH_ORDER = [
  "charter",
  "human_verdicts",
  "evals",
  "outcome_ledger",
  "marker_qa",
  "frozen_baselines",
] as const;

const nonblank = z.string().regex(/[^\s]/, "must not be blank");
const fraction = z.number().min(0).max(1);
const positiveInt = z.number().int().positive();
const nullablePositiveInt = positiveInt.nullable();
const nullableNonNegative = z.number().min(0).nullable();

const WriteTierSchema = z.enum(["T1", "T2", "T3"]);

const FloorSchema = z.strictObject({
  id: nonblank,
  metric: nonblank,
  op: z.enum([">=", "<="]),
  /** Null = not yet ruled, which is `needs_unmet`, never a default. */
  value: z.number().nullable(),
});

export const LearningCharterSchema = z.strictObject({
  schemaVersion: z.literal(CHARTER_SCHEMA_VERSION),
  bars: z.strictObject({
    patternMinSample: positiveInt,
    singleClientProvisional: z.strictObject({
      minThreads: positiveInt,
      ownerSignOff: z.literal(true),
      expiryDays: nullablePositiveInt,
    }),
    crossClientMinClients: positiveInt,
    worstOfK: z.strictObject({
      k: nullablePositiveInt,
      allMustPass: z.literal(true),
    }),
    repairRateReview: fraction,
    cosmeticDeltaMax: fraction,
    preRegistration: z.strictObject({
      proposeOnN: z.literal(true),
      confirmOnNPlus1: z.literal(true),
    }),
  }),
  floors: z.array(FloorSchema),
  tierPermissions: z.record(z.string(), WriteTierSchema),
  prohibitedMoves: z
    .array(z.enum(PROHIBITED_MOVE_IDS))
    .refine((ids) => new Set(ids).size === ids.length, "prohibitedMoves holds a duplicate")
    .refine(
      (ids) => PROHIBITED_MOVE_IDS.every((id) => ids.includes(id)),
      "prohibitedMoves must hold every id of the closed list (a charter cannot drop a prohibition)",
    ),
  selfGrill: z.strictObject({
    questions: z
      .array(z.strictObject({ id: z.enum(SELF_GRILL_QUESTION_IDS), text: nonblank }))
      .refine(
        (qs) =>
          qs.length === SELF_GRILL_QUESTION_IDS.length &&
          SELF_GRILL_QUESTION_IDS.every((id) => qs.some((q) => q.id === id)),
        "selfGrill.questions must hold each of the five question ids exactly once",
      ),
    unansweredIsNoShip: z.literal(true),
  }),
  truthOrder: z.tuple([
    z.literal("charter"),
    z.literal("human_verdicts"),
    z.literal("evals"),
    z.literal("outcome_ledger"),
    z.literal("marker_qa"),
    z.literal("frozen_baselines"),
  ]),
  needs: z.strictObject({
    ratingsRecencyDays: nullablePositiveInt,
    evalFreshnessMaxDays: nullablePositiveInt,
    minLabelerAccuracy: nullableNonNegative.refine((v) => v === null || v <= 1, "must be at most 1"),
    minBatchQuality: nullableNonNegative.refine((v) => v === null || v <= 1, "must be at most 1"),
  }),
});

export type LearningCharter = z.infer<typeof LearningCharterSchema>;

export type CharterParse =
  | { readonly ok: true; readonly charter: LearningCharter }
  | { readonly ok: false; readonly issues: readonly string[] };

/** Parse unknown content. Issues are paths and rules, never the content itself. */
export function parseLearningCharter(content: unknown): CharterParse {
  const r = LearningCharterSchema.safeParse(content);
  if (r.success) return { ok: true, charter: r.data };
  return {
    ok: false,
    issues: r.error.issues.map((i) => `${i.path.length > 0 ? i.path.join(".") : "(root)"}: ${i.message}`),
  };
}

/**
 * Where the charter disagrees with the code it was copied from. Empty means it
 * agrees. Each code is a path, so an operator can find the line to fix.
 */
export function charterCodeMismatches(charter: LearningCharter): string[] {
  const out: string[] = [];
  const expected: Readonly<Record<string, string>> = WRITE_TIERS;
  const held = charter.tierPermissions;
  for (const key of Object.keys(expected)) {
    if (!(key in held)) out.push(`tierPermissions.${key}:missing`);
    else if (held[key] !== expected[key]) out.push(`tierPermissions.${key}:differs`);
  }
  for (const key of Object.keys(held)) {
    if (!(key in expected)) out.push(`tierPermissions.${key}:unknown`);
  }
  if (charter.bars.patternMinSample !== PATTERN_CANDIDATE_MIN_SAMPLE) out.push("bars.patternMinSample:differs");
  if (charter.bars.repairRateReview !== REPAIR_RATE_REVIEW) out.push("bars.repairRateReview:differs");
  if (charter.bars.cosmeticDeltaMax !== COSMETIC_DELTA_MAX) out.push("bars.cosmeticDeltaMax:differs");
  return out;
}

/** Every required bar, floor or need that is still null (B15.5 "needs"). Empty means the learner may run. */
export function charterUnmetNeeds(charter: LearningCharter): string[] {
  const out: string[] = [];
  const b = charter.bars;
  if (b.singleClientProvisional.expiryDays === null) out.push("bars.singleClientProvisional.expiryDays");
  if (b.worstOfK.k === null) out.push("bars.worstOfK.k");
  charter.floors.forEach((f, i) => {
    if (f.value === null) out.push(`floors.${i}.${f.id}`);
  });
  for (const [name, value] of Object.entries(charter.needs)) {
    if (value === null) out.push(`needs.${name}`);
  }
  return out;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/** sha256 over key-sorted JSON, so the same content always hashes the same. */
export function charterContentHash(charter: LearningCharter): string {
  return createHash("sha256").update(canonical(charter)).digest("hex");
}

/** `charter-v3`. Versions are positive integers, active = highest (B16, rollback = publish old content as N+1). */
export const charterVersionLabel = (version: number): string => `charter-v${version}`;
